import { Typography, Box } from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { useTranslation } from "react-i18next";
import { startOfWeek, endOfWeek, eachDayOfInterval, format } from "date-fns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import ResponsiveCharts from "../ChartsLayout/ResponsiveCharts";
import CustomGridItem from "../ChartsLayout/CustomGridItem";
import { HiddenTypography, BarChartEmpty } from "../ChartsLayout/EmptyCharts";
// import { calculateWeeksAndMonthsWithData } from "../../../utils/filterDataByDate";

export default function ExerciseInsightsBarChart({
  exerciseData,
  exerciseInsights,
  selectedWeek,
}) {
  const weekStarts = startOfWeek(selectedWeek, { weekStartsOn: 1 });
  const weekEnds = endOfWeek(selectedWeek, { weekStartsOn: 1 });

  const weekDays = eachDayOfInterval({
    start: weekStarts,
    end: weekEnds,
  });

  const { t } = useTranslation("statistics");

  const getExerciseDataForADate = (date) => {
    const dateFormatted = format(date, "yyyy-MM-dd");
    const dayExerciseInsightsData = exerciseData.find(
      (data) => data.date === dateFormatted
    );
    return dayExerciseInsightsData
      ? {
          light: dayExerciseInsightsData.light,
          moderate: dayExerciseInsightsData.moderate,
          vigorous: dayExerciseInsightsData.vigorous,
          sedentary: dayExerciseInsightsData.sedentary,
        }
      : 0;
  };

  // const weeksMonthsWithData = calculateWeeksAndMonthsWithData(exerciseData);
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <ResponsiveCharts>
        {({ chartWidth }) => (
          <CustomGridItem>
            <Box ref={exerciseInsights}>
              <Typography
                sx={{
                  margin: {
                    xs: "20px 0",
                    sm: "20px 0",
                    md: "0 0 20px 0",
                    lg: "20px 0 0 20px",
                    xl: "20px",
                  },
                  textAlign: "center",
                }}
                variant="h6"
              >
                {t("charts.exercise.insightsTitle")}
              </Typography>

              {weekDays.some((day) => {
                const data = getExerciseDataForADate(day);
                return data && Object.values(data).some((val) => val > 0);
              }) ? (
                <>
                  <HiddenTypography />
                  <BarChart
                    xAxis={[
                      {
                        scaleType: "band",
                        data: weekDays.map((day) => format(day, "EEE")),
                        padding: 0.2,
                      },
                    ]}
                    series={[
                      {
                        data: weekDays.map(
                          (day) => getExerciseDataForADate(day).sedentary
                        ),
                        label: t("series.sedentaryMin"),
                        stack: "total",
                      },
                      {
                        data: weekDays.map(
                          (day) => getExerciseDataForADate(day).light
                        ),
                        label: t("series.lightMin"),
                        stack: "total",
                      },
                      {
                        data: weekDays.map(
                          (day) => getExerciseDataForADate(day).moderate
                        ),
                        label: t("series.moderateMin"),
                        stack: "total",
                      },
                      {
                        data: weekDays.map(
                          (day) => getExerciseDataForADate(day).vigorous
                        ),
                        label: t("series.totalExerciseMinutes"),
                        stack: "total",
                      },
                    ]}
                    width={chartWidth}
                    height={300}
                    slotProps={{
                      legend: {
                        direction: "row",
                        position: { vertical: "top", horizontal: "middle" },
                        padding: -5,
                        itemMarkWidth: 20,
                        itemMarkHeight: 3,
                        markGap: 5,
                        itemGap: 5,
                      },
                    }}
                  />
                </>
              ) : (
                <>
                  <BarChartEmpty weekDays={weekDays} chartWidth={chartWidth} />
                </>
              )}
            </Box>
          </CustomGridItem>
        )}
      </ResponsiveCharts>
    </LocalizationProvider>
  );
}
